import { ColumnsType } from 'antd/es/table';
import { ConfirmationModal, showAlertNotification, TableCommon } from 'components/common';
import { useAppDispatch, useAppSelector } from 'hooks';
import { InventoryDocumentItem } from 'models';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useCreateOrderSupplierMutation,
  useDeleteOrderSupplierMutation,
  useGetOrderSuppliersQuery,
  useLazyGetByIdOrderSupplierQuery,
} from 'services';
import { setTableParams, TableCacheItem } from 'stores/slices/tableCacheSlice';
import { OrderSupplierColumn, QueryParams, StatusOrderSupplierCode } from 'types';
import { formatPrice, useBarcodeUtils } from 'utils';
import { buildFullParams, parseSimpleFilter } from 'utils/filterBuilder';

const SupplierTable = ({ newColumns }: { newColumns: ColumnsType<OrderSupplierColumn> }) => {
  const navigate = useNavigate();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isPerformingOperation, setIsPerformingOperation] = useState(false);
  const [suppliers, setSuppliers] = useState<OrderSupplierColumn[]>([]);
  const pageKey = 'supplier-orders-list';
  const dispatch = useAppDispatch();
  const tableState = useAppSelector((s) => s.tableCache[pageKey]) as TableCacheItem<OrderSupplierColumn> | undefined;
  const [filteredQuery, setFilteredQuery] = useState<QueryParams>({
    include: 'status|supplier.baseUser',
    size: tableState?.pageSize ?? 10,
    page: tableState?.page ?? 1,
    sort: tableState?.sortField
      ? `${String(tableState.sortField)}:${tableState.sortOrder === 'descend' ? 'desc' : 'asc'}`
      : 'updatedAt:desc',
    searchKey: 'customerName|code|customerEmail|customerPhone',
    searchValue: tableState?.search ?? '',
    filter: tableState?.filter ?? 'type:eq:order-supplier',
  });
  const [orderSupplierChoice, setOrderSupplierChoice] = useState<OrderSupplierColumn | null>(null);
  const [confirmAction, setConfirmAction] = useState<'delete' | 'copy' | null>(null);
  const [createOrderSupplier] = useCreateOrderSupplierMutation();
  const [delteOrderSupllier] = useDeleteOrderSupplierMutation();
  const { handleCreateCode, handleBarcode, code, barcode } = useBarcodeUtils({
    code: 'OS',
  });
  const { data, isLoading, isError } = useGetOrderSuppliersQuery({
    query: {
      include: 'status|supplier.baseUser',
      ...filteredQuery,
    } as QueryParams,
  });
  const [triggerGetOrderSupplierById] = useLazyGetByIdOrderSupplierQuery();

  const getStatusCodeKey = (value: string): keyof typeof StatusOrderSupplierCode | '' => {
    for (const [key, val] of Object.entries(StatusOrderSupplierCode)) {
      if (val === value) return key as keyof typeof StatusOrderSupplierCode;
    }
    return '';
  };
  useEffect(() => {
    if (data && !isError) {
      const mapSupplierData = data?.data?.rows?.flatMap((item) => {
        return [
          {
            key: item.id ?? '',
            code: item.code ?? '',
            'supplier.baseUser.name': item.supplier?.baseUser?.name ?? `--`,
            'status.name': getStatusCodeKey(item.status?.name ?? ''),
            'status.id': item.statusId,
            createdAt: item.createdAt
              ? new Date(item.createdAt).toLocaleString('vi-VN', {
                  hour: '2-digit',
                  minute: '2-digit',
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour12: false,
                })
              : '',
            total: item.total ? `${formatPrice(item?.total)} VNĐ` : 0,
            payInAdvance: item.payInAdvance ? `${formatPrice(item?.payInAdvance)} VNĐ` : 0,
            paidMoney: item.paidMoney ? `${formatPrice(item?.paidMoney)} VNĐ` : 0,
            unpaidMoney: item.payInAdvance ? `${formatPrice(Number(item.total) - Number(item.payInAdvance))} VNĐ` : 0,
          },
        ];
      }) as OrderSupplierColumn[];

      setSuppliers(mapSupplierData || []);
      dispatch(
        setTableParams({
          pageKey,
          params: {
            lastFetched: Date.now(),
            search: filteredQuery.searchValue ?? '',
            page: filteredQuery.page ?? 1,
            pageSize: filteredQuery.size ?? 10,
            filter: filteredQuery.filter ?? '',
            pageKey,
          } as unknown as Partial<TableCacheItem<unknown>>,
        }),
      );
    }
  }, [data, isError]);

  const handleEdit = (supplier: OrderSupplierColumn) => {
    navigate(`/management/inventory/supplier-orders/edit/${supplier.key}`, {
      state: { orderSupplierId: supplier.key },
    });
  };
  const handleView = (supplier: OrderSupplierColumn) => {
    navigate(`/management/inventory/supplier-orders/view/${supplier.key}`, {
      state: { orderSupplierId: supplier.key },
    });
  };
  const handleDelete = useCallback((orderSupplier: OrderSupplierColumn) => {
    setOrderSupplierChoice(orderSupplier);
    setConfirmAction('delete');
    setIsConfirmModalOpen(true);
  }, []);

  const handleCopy = useCallback(
    async (orderSupplier: OrderSupplierColumn) => {
      setOrderSupplierChoice(orderSupplier);
      setConfirmAction('copy');
      setIsConfirmModalOpen(true);
      await handleCreateCode();
      handleBarcode();
    },
    [handleBarcode, handleCreateCode],
  );

  const confirmCopy = useCallback(async () => {
    try {
      if (!orderSupplierChoice) {
        throw new Error('No order supplier selected');
      }
      const result = await triggerGetOrderSupplierById({
        orderSupplierId: orderSupplierChoice.key,
        query: {
          include: 'inventoryDocumentItems|status',
        },
      }).unwrap();
      if (result.success) {
        const payload = {
          id: crypto.randomUUID(),
          isPublishing: result.data.status?.name !== StatusOrderSupplierCode.DRAFT_PAYMENT,
          code: code,
          barcode: barcode,
          deliveryDate: result.data.deliveryDate,
          receivedDate: result.data.receivedDate,
          total: Number(result.data.total),
          supplierId: result.data.supplierId,
          description: result.data.description,
          note: result.data.describeNote,
          userName: result.data.userName,
          userPhoneNumber: result.data.userPhoneNumber,
          userEmail: result.data.userEmail,
          currentStep: result.data.currentStep,
          statusId: result.data.statusId,
          taxId: result.data.taxId,
          warehouseId: result.data.warehouseId,
          paymentType: result.data.paymentType,
          createdBy: null,
          type: result.data.type,
          inventoryDocumentId: result.data.inventoryDocumentId,
          items: result.data.items,
          payInAdvance: Number(result.data.payInAdvance),
          paidMoney: result.data.paidMoney,
          unpaidMoney: result.data.unpaidMoney,
          inventoryDocumentItems:
            result.data.inventoryDocumentItems?.map((item: InventoryDocumentItem) => ({
              ...item,
              costPrice: Number(item.costPrice),
              salePrice: Number(item.salePrice),
              total: Number(item.total),
            })) ?? [],
        };
        await createOrderSupplier(payload).unwrap();
        showAlertNotification({
          type: 'success',
          title: 'Copy đơn hàng thành công',
          message: `Bạn đã copy đơn hàng thành công từ mã đơn ${result.data.code} sang ${code}`,
        });
      }
    } catch (error) {
      console.error('Lỗi khi gọi API: ', error);
      showAlertNotification({
        type: 'error',
        title: 'Lỗi ghi lấy dữ liệu đơn hàng',
        message: `bLỗi ghi lấy dữ liệu đơn hàng`,
      });
    }
  }, [code, barcode, triggerGetOrderSupplierById, createOrderSupplier, orderSupplierChoice]);

  const confirmDelete = async () => {
    if (!orderSupplierChoice?.key || typeof orderSupplierChoice.key !== 'string') return;

    try {
      await delteOrderSupllier(orderSupplierChoice.key).unwrap();
      showAlertNotification({
        type: 'success',
        title: 'Xóa đơn hàng thành công',
        message: `Đơn hàng ${orderSupplierChoice?.code} đã được xóa`,
      });
    } catch (error) {
      console.log('error', error);
      showAlertNotification({
        type: 'error',
        title: 'Xóa Đơn hàng thất bại',
        message: `Đơn hàng ${orderSupplierChoice?.code} xóa thất bại`,
      });
    } finally {
      setIsConfirmModalOpen(false);
      setOrderSupplierChoice(null);
    }
  };
  const onConfirmAction = async () => {
    if (!orderSupplierChoice) return;
    setIsPerformingOperation(true);

    try {
      if (confirmAction === 'delete') {
        await confirmDelete();
      } else if (confirmAction === 'copy') {
        await confirmCopy();
      }
    } finally {
      setIsPerformingOperation(false);
      setIsConfirmModalOpen(false);
      setOrderSupplierChoice(null);
      setConfirmAction(null);
    }
  };

  const confirmModalConfig = {
    delete: {
      title: 'Xác nhận xóa đơn hàng',
      subtitle: (
        <div>
          Bạn có chắc chắn muốn xóa đơn hàng này không?
          <br />
          Nếu có hãy chọn vào nút đồng ý. Thao tác này sẽ không thể hoàn tác.
        </div>
      ),
      icon: 'warning',
      confirmText: 'Xóa',
    },
    copy: {
      title: 'Xác nhận sao chép đơn hàng',
      subtitle: (
        <div>
          Bạn có chắc chắn muốn sao chép đơn hàng này không?
          <br />
          Đơn hàng mới sẽ được tạo từ thông tin của đơn hàng cũ.
        </div>
      ),
      icon: 'warning',
      confirmText: 'Sao chép',
    },
  }[confirmAction ?? 'delete'];
  const handlePagination = (query: QueryParams) => {
    try {
      const columnEqs = ['name', 'phone', 'type'];
      const columnArray = ['country', 'status'];
      const filterQuery = buildFullParams(
        {
          ...query,
          type: 'order-supplier',
        },
        columnEqs,
        columnArray,
      );
      setFilteredQuery(filterQuery);
    } catch (error) {
      console.log('handlePagination: ', error);
    }
  };

  return (
    <div className='bg-[var(--textWhite)] supplier-table'>
      <TableCommon<OrderSupplierColumn>
        columns={newColumns}
        dataSource={suppliers}
        defaultPageSize={10}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onCopy={handleCopy}
        tableProps={{
          scroll: { x: 'max-content' },
          size: 'large',
          className: 'shadow-sm',
          style: {
            minHeight: 'calc(100vh - 385px)',
          },
          loading: isLoading,
        }}
        count={data?.data?.count}
        onPaginationChange={handlePagination}
        currentPage={Number(filteredQuery.page)}
        searchColumnDefault={parseSimpleFilter(tableState?.filter)}
      />
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onCancel={() => {
          setIsConfirmModalOpen(false);
          setOrderSupplierChoice(null);
          setConfirmAction(null);
        }}
        onConfirm={onConfirmAction}
        title={confirmModalConfig.title}
        subtitle={confirmModalConfig.subtitle}
        icon='warning'
        confirmText={confirmModalConfig.confirmText}
        confirmLoading={isPerformingOperation}
      />
    </div>
  );
};

export default SupplierTable;
